import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { rolesService } from "../../../services/rolesService";
import { AdminLayout } from "../../layout/AdminLayout";
import { DataTable } from "../../../components/DataTable";
import { toast } from "react-toastify";
import { Edit, Trash2, Plus } from "lucide-react";

export const RolesList = () => {
	const [roles, setRoles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [sortBy, setSortBy] = useState("name");
	const [sortOrder, setSortOrder] = useState("asc");

	useEffect(() => {
		loadRoles();
	}, [page, pageSize, search, sortBy, sortOrder]);

	const loadRoles = async () => {
		try {
			setLoading(true);
			const data = await rolesService.getAll(
				page,
				pageSize,
				search,
				sortBy,
				sortOrder,
			);
			setRoles(data.data || []);
			setTotal(data.total || 0);
		} catch (error) {
			console.log("Error loading roles:", error);
			toast.error("Failed to load roles");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id) => {
		if (!confirm("Are you sure?")) return;

		try {
			await rolesService.delete(id);
			toast.success("Role deleted");
			loadRoles();
		} catch (error) {
			console.log("Error deleting role:", error);
			toast.error("Failed to delete role");
		}
	};

	const columns = [
		{
			key: "name",
			label: "Name",
			sortable: true,
			width: "25%",
		},
		{
			key: "description",
			label: "Description",
			sortable: false,
			width: "40%",
			render: (row) => row.description || "-",
		},
		{
			key: "actions",
			label: "Actions",
			sortable: false,
			width: "20%",
			render: (row) => (
				<div className="flex gap-2">
					<Link
						to={`/admin/roles/${row._id}`}
						className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
					>
						<Edit size={16} />
						Edit
					</Link>
					<button
						onClick={() => handleDelete(row._id)}
						className="text-red-600 hover:text-red-800 flex items-center gap-1"
					>
						<Trash2 size={16} />
						Delete
					</button>
				</div>
			),
		},
	];

	return (
		<AdminLayout>
			<div className="p-6">
				<div className="flex justify-between items-center mb-6">
					<h1 className="text-3xl font-bold text-gray-800">Roles</h1>
					<Link
						to="/admin/roles/new"
						className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
					>
						<Plus size={20} />
						Add Role
					</Link>
				</div>

				<DataTable
					columns={columns}
					data={roles}
					loading={loading}
					totalItems={total}
					currentPage={page}
					pageSize={pageSize}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
					onSearch={setSearch}
					onSort={(column, order) => {
						setSortBy(column);
						setSortOrder(order);
					}}
					searchPlaceholder="Search by name or description..."
					sortBy={sortBy}
					sortOrder={sortOrder}
				/>
			</div>
		</AdminLayout>
	);
};
